"use client";

import { useEffect, useRef } from "react";

interface ToastProps {
  message: string;
  type?: "success" | "error" | "info";
  isVisible: boolean;
  onDismiss: () => void;
  duration?: number;
  action?: { label: string; onClick: () => void };
}

export default function Toast({
  message,
  type = "info",
  isVisible,
  onDismiss,
  duration,
  action,
}: ToastProps) {
  // Keep onDismiss in a ref so the timer effect doesn't restart on every
  // parent re-render (the inline arrow creates a new reference each time).
  const onDismissRef = useRef(onDismiss);
  useEffect(() => { onDismissRef.current = onDismiss; }, [onDismiss]);

  useEffect(() => {
    if (!isVisible) return;
    const ms = duration ?? (type === "error" ? 6000 : 3000);
    const timer = setTimeout(() => onDismissRef.current(), ms);
    return () => clearTimeout(timer);
  }, [isVisible, message, type, duration]);

  if (!isVisible) return null;

  const classMap: Record<string, string> = {
    success: "toast--success",
    error: "toast--error",
    info: "toast--warning",
  };

  return (
    <div className={`toast ${classMap[type] || "toast--warning"}`} role="status">
      {message}
      {action && (
        <button
          onClick={action.onClick}
          style={{
            marginLeft: 12,
            padding: "2px 10px",
            borderRadius: 4,
            border: "1px solid currentColor",
            background: "transparent",
            color: "inherit",
            cursor: "pointer",
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
